import { localizedProductPath } from './localized-paths';

export type ArtworkPrintProduct = {
  slug: string;
  label: string;
  shortLabel: string;
  description: string;
  sizes: Array<{ key: string; label: string; widthMm: number; heightMm: number }>;
};

export const ARTWORK_PRINT_PRODUCTS: ArtworkPrintProduct[] = [
  {
    slug: 'canvas',
    label: 'Canvas',
    shortLabel: 'op canvas',
    description: 'Museumachtige uitstraling op gespannen canvas met zichtbaar doekstructuur.',
    sizes: [
      { key: '30x40', label: '30 x 40 cm', widthMm: 300, heightMm: 400 },
      { key: '40x60', label: '40 x 60 cm', widthMm: 400, heightMm: 600 },
      { key: '60x80', label: '60 x 80 cm', widthMm: 600, heightMm: 800 },
      { key: '80x120', label: '80 x 120 cm', widthMm: 800, heightMm: 1200 },
    ],
  },
  {
    slug: 'ingelijst-canvas',
    label: 'Canvas met baklijst',
    shortLabel: 'met baklijst',
    description: 'Canvasprint met luxe zwevende lijst voor een galeriegevoel.',
    sizes: [
      { key: '30x40', label: '30 x 40 cm', widthMm: 300, heightMm: 400 },
      { key: '40x60', label: '40 x 60 cm', widthMm: 400, heightMm: 600 },
      { key: '60x80', label: '60 x 80 cm', widthMm: 600, heightMm: 800 },
    ],
  },
  {
    slug: 'foto-op-poster',
    label: 'Poster',
    shortLabel: 'als poster',
    description: 'Scherpe kunstposter voor lijst, wand of wisselende collectie.',
    sizes: [
      { key: 'a4', label: 'A4', widthMm: 210, heightMm: 297 },
      { key: 'a3', label: 'A3', widthMm: 297, heightMm: 420 },
      { key: 'a2', label: 'A2', widthMm: 420, heightMm: 594 },
      { key: '50x70', label: '50 x 70 cm', widthMm: 500, heightMm: 700 },
    ],
  },
  {
    slug: 'ingelijste-poster',
    label: 'Ingelijste poster',
    shortLabel: 'ingelijst',
    description: 'Poster met lijst, klaar om op te hangen in huis of kantoor.',
    sizes: [
      { key: 'a4', label: 'A4', widthMm: 210, heightMm: 297 },
      { key: 'a3', label: 'A3', widthMm: 297, heightMm: 420 },
      { key: '50x70', label: '50 x 70 cm', widthMm: 500, heightMm: 700 },
    ],
  },
  {
    slug: 'fototegel',
    label: 'Fototegel',
    shortLabel: 'op fototegel',
    description: 'Compacte vierkante kunsttegel voor kleine wandcomposities.',
    sizes: [
      { key: '15x15', label: '15 x 15 cm', widthMm: 150, heightMm: 150 },
      { key: '20x20', label: '20 x 20 cm', widthMm: 200, heightMm: 200 },
    ],
  },
  {
    slug: 'behangcirkel',
    label: 'Behangcirkel',
    shortLabel: 'als behangcirkel',
    description: 'Ronde wanddecoratie met opvallende uitsnede van het kunstwerk.',
    sizes: [
      { key: '60', label: 'Ø 60 cm', widthMm: 600, heightMm: 600 },
      { key: '100', label: 'Ø 100 cm', widthMm: 1000, heightMm: 1000 },
    ],
  },
];

export function artworkSlug(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 90);
}

export function artworkProductSlug(artworkSlugValue: string, productSlug: string) {
  const product = ARTWORK_PRINT_PRODUCTS.find((item) => item.slug === productSlug);
  const suffix = product?.shortLabel
    .replace(/^op\s+/, 'op-')
    .replace(/^als\s+/, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/gi, '')
    || productSlug;
  const cleanSuffix = artworkSlug(suffix);
  const baseMaxLength = Math.max(30, 90 - cleanSuffix.length - 1);
  const cleanBase = artworkSlug(artworkSlugValue).slice(0, baseMaxLength).replace(/-+$/g, '');
  return `${cleanBase}-${cleanSuffix}`;
}

export function isCommercialArtworkLicense(licenseUrl?: string | null, rightsText?: string | null, license?: string | null) {
  const haystack = [licenseUrl, rightsText, license].filter(Boolean).join(' ').toLowerCase();
  return (
    haystack.includes('creativecommons.org/publicdomain/zero') ||
    haystack.includes('creativecommons.org/publicdomain/mark') ||
    haystack.includes('public domain') ||
    haystack.includes('publiek domein') ||
    haystack.includes('cc0')
  );
}

export function artworkImageUrl(artwork: { imageUrl?: string | null; iiifImageId?: string | null; iiifImageUrl?: string | null }, width = 1400) {
  if (artwork.iiifImageId) return `https://iiif.micr.io/${artwork.iiifImageId}/full/${width},/0/default.jpg`;
  if (artwork.iiifImageUrl) return artwork.iiifImageUrl;
  return artwork.imageUrl || '/images/products/mockup-wallart.svg';
}

export function artworkOrderUrl(locale: string, productSlug: string, artwork: { slug: string }, sizeKey: string, fitMode: string) {
  const params = new URLSearchParams({
    artwork: artwork.slug,
    size: sizeKey,
    fit: fitMode,
  });
  return `${localizedProductPath(locale, productSlug)}?${params.toString()}#configurator`;
}

export function requiredPixelsForSize(size: { widthMm: number; heightMm: number }, minDpi = 120) {
  const widthInch = size.widthMm / 25.4;
  const heightInch = size.heightMm / 25.4;
  return {
    width: Math.ceil(widthInch * minDpi),
    height: Math.ceil(heightInch * minDpi),
  };
}

export function isSizeLikelyPrintable(
  artwork: { widthPx?: number | null; heightPx?: number | null },
  size: { widthMm: number; heightMm: number },
  minDpi = 120
) {
  if (!artwork.widthPx || !artwork.heightPx) return true;
  const required = requiredPixelsForSize(size, minDpi);
  const longImage = Math.max(artwork.widthPx, artwork.heightPx);
  const shortImage = Math.min(artwork.widthPx, artwork.heightPx);
  const longRequired = Math.max(required.width, required.height);
  const shortRequired = Math.min(required.width, required.height);
  return longImage >= longRequired && shortImage >= shortRequired;
}
